<script>
import Modal from "@/components/g-modal.vue";
import { mapGetters } from "vuex";
import { TipoVPLEnum } from '@/core/enums/ativacaoped';

/** @typedef Header
 * @property {string} [text]
 * @property {string} [value]
 * @property {string} [align]
 * @property {boolean} [sortable]
 */

/**
 * @this {Vue & { clientId: number }}
 */
export default {
  components: {
    Modal
  },
  props: {
    businessPlanId: {
      type: [String, Number],
      required: true
    },
    opened: Boolean,
    rubricas: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    /** @type {import('../../businessplan.types').Rubrica[]} */
    const selection = this.rubricas.filter(({ id }) => this.value.includes(id));

    return {
      /** @type {boolean} */
      innerOpened: this.opened,
      /** @type {string} */
      search: "",
      selection
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    attatchResource() {
      return this.apiResource(
        `v1/ativacaoped/${this.clientId}/business-plans/${this.businessPlanId}/vpl/rubrica/vincular-rubricas`
      );
    },
    disabledAttatchBtn() {
      return !this.selection.length;
    },
    /**
     * @type {Header[]}
     * @readonly
     */
    headers() {
      return [
        { text: "", value: "selection", align: "center", sortable: false },
        { text: "Tipo", value: "tipo", width: "160px" },
        { text: "Descrição", value: "descricao" }
      ];
    },
    rows() {
      return this.rubricas.filter(({ tipo_id }) => tipo_id !== TipoVPLEnum.CUSTO);
    }
  },
  watch: {
    innerOpened(value) {
      this.$emit("update:opened", value);
    },
    opened(value) {
      if (value !== this.innerOpened) {
        this.innerOpened = value;

        if (value) {
          this.selection = this.rubricas.filter(({ id }) =>
            this.value.includes(id)
          );
        }
      }
    }
  },
  methods: {
    close() {
      this.innerOpened = false;
      this.$emit("update:opened", false);
    },
    async doDetatch() {
      try {
        const selectedIds = this.selection.map(({ id }) => id);
        const rubricasIds = this.rubricas.map(({ id }) => id);
        const rubricas = rubricasIds.filter(id => !selectedIds.includes(id));

        await this.attatchResource.save({ rubricas });

        const title =
          selectedIds.length > 1
            ? "Retornos financeiros desvinculados com sucesso"
            : "Retorno financeiro desvinculado com sucesso";
        const text =
          selectedIds.length > 1
            ? `${selectedIds.length} retornos financeiros foram desvinculados do Business Plan.`
            : `Um retorno financeiro foi desvinculado do Business Plan.`;
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title,
          text
        });
        this.$emit("reload");
        this.innerOpened = false;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    }
  }
};
</script>

<template>
  <Modal
    v-model="innerOpened"
    appendTitleIcon="mdi-link-plus"
    contentClass="vpl-detatch-modal"
    title="Remover Retornos Financeiros"
    width="800px"
  >
    <v-data-table
      v-model="selection"
      class="vpl-attach-data-table"
      disable-pagination
      fixed-header
      :headers="headers"
      hide-default-footer
      :items="rows"
      :search="search"
      show-select
    >
      <template v-slot:top>
        <div class="sticky-top">
          <v-text-field
            v-model="search"
            class="search-field"
            clearable
            dense
            outlined
            placeholder="Filtrar..."
            prepend-inner-icon="mdi-filter-outline"
            hide-details
          ></v-text-field>
        </div>
      </template>
    </v-data-table>

    <template #buttons>
      <v-spacer />

      <v-btn class="px-5 ml-3" color="secondary" depressed @click="close">
        Cancelar
      </v-btn>

      <v-btn
        class="px-5 ml-3"
        color="primary"
        :dark="!disabledAttatchBtn"
        depressed
        :disabled="disabledAttatchBtn"
        @click="doDetatch"
      >
        Desvincular
      </v-btn>
    </template>
  </Modal>
</template>

<style lang="scss">
.vpl-detatch-modal {
  & > .v-card > .v-card__text {
    padding: 0;
    overflow: hidden;
  }
}
</style>

<style lang="scss" scoped>
.search-field {
  margin: 0;
}

.vpl-attach-data-table {
  display: flex;
  flex-direction: column;
  max-height: 60vh;

  &::v-deep .v-data-table__wrapper {
    flex: 1;

    .tbody {
      flex: 1;
      overflow-y: auto;
    }
  }
}

.sticky-top {
  flex: 0;
  background: white;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
}
</style>
